import React from 'react';
import { Clock, Layers, Users, Play, Square, CheckCircle2, Zap } from 'lucide-react';
import { TelegramListItem, Avatar } from '@tadagram/ui';

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  nodeCount?: number;
  edgeCount?: number;
  assignedAccounts?: number;
  campaignId?: string;
  updatedAt?: string;
  isActive?: boolean;
  isCompleted?: boolean;  // One-shot workflow has finished
  runType?: 'recurring' | 'one_shot';
}

interface WorkflowItemProps {
  workflow: Workflow;
  active: boolean;
  onClick: () => void;
}

const formatUpdated = (updated?: string): string => {
  if (!updated) return 'No update';
  const d = new Date(updated);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

/** Show first 4 and last 4 chars of ID with ellipsis in the middle. */
const truncateMid = (id: string): string => {
  if (id.length <= 11) return id;
  return `${id.slice(0, 4)}...${id.slice(-4)}`;
};

const WorkflowItem: React.FC<WorkflowItemProps> = ({ workflow, active, onClick }) => {
  const updatedText = formatUpdated(workflow.updatedAt);

  return (
    <TelegramListItem
      onClick={onClick}
      active={active}
      title={workflow.name}
      subtitle={
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={12} />
            {updatedText}
          </span>
          <span style={{ fontSize: 10, color: '#9ca3af', letterSpacing: '0.3px', fontFamily: 'monospace', whiteSpace: 'nowrap', display: 'block' }}>
            {truncateMid(workflow.id)}
          </span>
        </span>
      }
      avatar={
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Avatar variant={workflow.isActive ? "success" : workflow.isCompleted ? "primary" : "danger"}>
            {workflow.isActive ? (
              <Play size={18} fill="currentColor" />
            ) : workflow.isCompleted ? (
              <CheckCircle2 size={16} />
            ) : (
              <Square size={16} fill="currentColor" />
            )}
          </Avatar>
          <span style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.5px',
            color: workflow.isActive ? '#16a34a' : workflow.isCompleted ? '#2563eb' : '#dc2626',
            lineHeight: 1,
          }}>
            {workflow.isActive ? 'RUNNING' : workflow.isCompleted ? 'DONE' : 'STOPPED'}
          </span>
        </div>
      }
      meta={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 13 }}>
          {workflow.runType === 'one_shot' && (
            <span title="Run Once" style={{ display: 'flex', alignItems: 'center', gap: 2, color: '#d97706' }}>
              <Zap size={13} />
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Users size={14} />
            {workflow.assignedAccounts || 0}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Layers size={14} />
            {workflow.nodeCount || 0}
          </span>
        </div>
      }
    />
  );
};

export default WorkflowItem;
